import { KeyboardEvent, useCallback, useState } from 'react';
import { useChat } from '../../hooks/useChat';

const MAX_LENGTH = 1000;

// Spinning loader for send button
function Spinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

interface Props {
  inputRef: React.RefObject<HTMLTextAreaElement>;
}

export function ChatInput({ inputRef }: Props) {
  const { sendMessage, isSending } = useChat();
  const [text, setText] = useState('');

  // Keep textarea height in sync with content
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const val = e.target.value;
      if (val.length > MAX_LENGTH) return;
      setText(val);
      // Reset height then set to scrollHeight so it shrinks on delete too
      e.target.style.height = 'auto';
      e.target.style.height = `${Math.min(e.target.scrollHeight, 128)}px`;
    },
    [],
  );

  const submit = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || isSending) return;
    setText('');
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    await sendMessage(trimmed);
  }, [text, isSending, sendMessage, inputRef]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    },
    [submit],
  );

  const nearLimit = text.length >= MAX_LENGTH - 100;
  const canSend = text.trim().length > 0 && !isSending;

  return (
    <div className="border-t border-gray-100 bg-white px-3 pb-3 pt-2">
      <div className="flex items-end gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 transition-colors focus-within:border-[#53978e] focus-within:ring-1 focus-within:ring-[#53978e]/30">
        <textarea
          ref={inputRef}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={isSending}
          rows={1}
          placeholder="Type your message…"
          aria-label="Message input"
          className="flex-1 resize-none bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none disabled:opacity-60"
          style={{ maxHeight: '128px', overflowY: 'auto' }}
        />

        <button
          onClick={submit}
          disabled={!canSend}
          aria-label="Send message"
          className="mb-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#53978e] text-white transition-all hover:bg-[#3f7a72] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {isSending ? (
            <Spinner />
          ) : (
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
            </svg>
          )}
        </button>
      </div>

      {/* Character counter — only near the limit */}
      {nearLimit && (
        <p className="mt-1 text-right text-[10px] text-gray-400">
          {text.length}/{MAX_LENGTH}
        </p>
      )}
    </div>
  );
}
